import { http } from './client';
import type { AuditChannel, AuditRecord, AuditStatus, PageResult } from './types';

export interface AuditFilterParams {
  username?: string;
  intent?: string;
  channel?: AuditChannel;
  status?: AuditStatus[];
  since?: string;
  until?: string;
}

export interface ListAuditParams extends AuditFilterParams {
  limit?: number;
  cursor?: string;
}

function toQuery<T extends AuditFilterParams>({ status, ...rest }: T): Omit<T, 'status'> & { status?: string } {
  return status?.length ? { ...rest, status: status.join(',') } : rest;
}

export const auditApi = {
  async list(params: ListAuditParams = {}): Promise<PageResult<AuditRecord>> {
    const res = await http.get<PageResult<AuditRecord>>('/v1/bot/audit', { params: toQuery(params) });
    return res.data;
  },

  async exportCsv(params: AuditFilterParams = {}): Promise<Blob> {
    const res = await http.get<Blob>('/v1/bot/audit/export', {
      params: toQuery(params),
      responseType: 'blob',
    });
    return res.data;
  },

  async get(sessionId: string): Promise<{ sessionId: string; entries: AuditRecord[] }> {
    const res = await http.get<{ sessionId: string; entries: AuditRecord[] }>(
      `/v1/bot/audit/${encodeURIComponent(sessionId)}`,
    );
    return res.data;
  },

  async delete(sessionId: string): Promise<{ deleted: true; sessionId: string }> {
    const res = await http.delete<{ deleted: true; sessionId: string }>(
      `/v1/bot/audit/${encodeURIComponent(sessionId)}`,
    );
    return res.data;
  },

  async byOrigin(
    params: { since?: string; until?: string } = {},
  ): Promise<{ items: Array<{ origin: string; count: number; lastSeen: string }> }> {
    const res = await http.get<{
      items: Array<{ origin: string; count: number; lastSeen: string }>;
    }>('/v1/bot/audit/by-origin', { params });
    return res.data;
  },
};
